import { createContext, useContext, type ReactNode } from 'react';

/** The signed-in staff member, as returned by GET /staff/me. */
export interface StaffMe {
  user_id: string;
  email: string | null;
  display_name: string;
  roles: { role: string; location_id: string | null }[];
  permissions: string[];
  /** Active locations this person works at (older APIs leave it out). */
  locations?: StaffLocation[];
  aal: string | null;
  mfa_enforced: boolean;
}

export interface StaffLocation {
  id: string;
  slug: string;
  name: string;
}

/** Before /staff/me answers with locations: the first location, as it has always been. */
const FIRST_LOCATION: StaffLocation = { id: '', slug: 'mobile-1', name: 'Crave Pizza' };

/** localStorage key for the location this device last worked on. */
export const LOCATION_KEY = 'crave.adminLocation';

export function readSavedLocation(): string | null {
  try {
    return localStorage.getItem(LOCATION_KEY);
  } catch {
    return null;
  }
}

export function saveLocation(slug: string): void {
  try {
    localStorage.setItem(LOCATION_KEY, slug);
  } catch {
    // Private windows: the choice lasts for this visit only.
  }
}

/** Query key prefix for /staff/me. Invalidate it after anything that changes the session level. */
export const STAFF_ME_KEY = 'staff-me';

export interface StaffContextValue {
  permissions: ReadonlySet<string>;
  aal: string | null;
  mfaEnforced: boolean;
  /** True when this staff member holds the permission. */
  can: (permission: string) => boolean;
  /** True when the API will refuse two-factor-gated actions for this session. */
  needsMfa: boolean;
  /** The location every page shows. Pages pass `location.slug` to the API. */
  location: StaffLocation;
  locations: StaffLocation[];
  setLocation: (slug: string) => void;
}

const EMPTY: StaffContextValue = {
  permissions: new Set(),
  aal: null,
  mfaEnforced: false,
  can: () => false,
  needsMfa: false,
  location: FIRST_LOCATION,
  locations: [],
  setLocation: () => {},
};

const StaffContext = createContext<StaffContextValue>(EMPTY);

export function staffContextValue(
  me: StaffMe,
  chosenSlug: string | null = null,
  setLocation: (slug: string) => void = () => {},
): StaffContextValue {
  const permissions = new Set(me.permissions);
  const locations = me.locations ?? [];
  const location = locations.find((l) => l.slug === chosenSlug) ?? locations[0] ?? FIRST_LOCATION;
  return {
    location,
    locations,
    setLocation,
    permissions,
    aal: me.aal,
    mfaEnforced: me.mfa_enforced,
    can: (p) => permissions.has(p),
    needsMfa: me.mfa_enforced && me.aal !== 'aal2',
  };
}

export function StaffProvider({
  value,
  children,
}: {
  value: StaffContextValue;
  children: ReactNode;
}) {
  return <StaffContext.Provider value={value}>{children}</StaffContext.Provider>;
}

/** Permissions and session level of the signed-in staff member. Empty outside the admin shell. */
export function useStaff(): StaffContextValue {
  return useContext(StaffContext);
}
